import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import CapabilityShowcase from '../components/CapabilityShowcase';
import LoadingIndicator from '../components/LoadingIndicator';

const WelcomePage = () => {
  const navigate = useNavigate();
  const useCaseRef = useRef(null);

  // Set body background and override safe-area padding for this page
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    const originalHtmlPadding = document.documentElement.style.padding;
    
    document.body.style.backgroundColor = '#000';
    // Override the safe-area padding that causes the jump
    document.documentElement.style.padding = '0';
    
    return () => {
      document.body.style.backgroundColor = originalBg;
      document.documentElement.style.padding = originalHtmlPadding;
    };
  }, []);

  return (
    <div className="dark fixed inset-0 flex flex-col bg-black">
      {/* Header - NOT fixed, just at top of flex container */}
      <header className="flex-shrink-0 bg-black z-50">
        <div className="max-w-[1400px] mx-auto px-3 lg:px-6 py-2 lg:py-4 flex items-center justify-between">
          <img 
            src="/logo_white.webp" 
            alt="OKIO" 
            className="h-5 md:h-6 w-auto"
          />
          <a
            href="mailto:hello@okio.ai"
            className="text-xs md:text-sm font-medium text-white hover:opacity-70 transition-opacity border border-white/30 rounded-full px-3 md:px-4 py-1.5 md:py-2"
          >
            Contact
          </a>
        </div>
      </header>
      
      {/* Scrollable content container */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">

      {/* ============================================
          SLIDE 1: Hero
          ============================================ */}
      <section className="min-h-[calc(100vh-52px)] flex items-start lg:items-center bg-black">
        <div className="max-w-[1400px] mx-auto px-6 py-8 lg:py-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 lg:gap-0 items-center">
            {/* Text content: Shows second on mobile, first on desktop */}
            {/* Left aligned on mobile + desktop, centered on tablet */}
            <div className="max-w-xl order-2 lg:order-1 md:text-center lg:text-left md:mx-auto lg:mx-0">
              <h1 className="text-4xl sm:text-5xl 2xl:text-6xl font-bold text-[#1a1a18] dark:text-[#FAFAF8] leading-[1.05] sm:leading-[1.1] mb-4 lg:mb-6 max-w-lg 2xl:max-w-xl md:mx-auto lg:mx-0 tracking-tight">
                AI Agent for Real-World Operations
              </h1>
              <p className="text-lg md:text-xl 2xl:text-2xl font-medium text-[#a0a095] mb-8 max-w-md 2xl:max-w-lg md:mx-auto lg:mx-0 leading-snug md:leading-relaxed">
                Helps organizations reliably guide people through complex tasks in real situations.
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=ai.okio.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white text-black font-medium rounded-xl hover:bg-white/90 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                Try the App
              </a>
            </div>
            
            {/* Image: Shows first on mobile, second on desktop */}
            <div className="flex items-center justify-center order-1 lg:order-2 -mx-7 lg:mx-0">
              <div className="relative w-full aspect-[16/10] max-w-xl lg:max-w-6xl">
                {/* Collage illustration background */}
                <img 
                  src="/hero-collage-illustration.webp" 
                  alt="AI guidance across real-world tasks"
                  className="w-full h-full object-contain"
                />
                {/* Animated eyes positioned in the center circle */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="scale-[2.5] sm:scale-[3.5] lg:scale-[4.5] transform-gpu">
                    <LoadingIndicator className="!mt-0 !py-0 !px-0" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SLIDES 2-5: Capability Showcase
          ============================================ */}
      <div ref={useCaseRef}>
        <CapabilityShowcase />
      </div>

      {/* Final CTA */}
      <section className="relative py-32 md:py-40 px-6">
        <div className="absolute inset-0">
          <img 
            src="/home/photo5.jpeg" 
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight font-bold">
            Ready to get started?
          </h2>
          <p className="text-lg md:text-xl mb-12 text-white/80 max-w-xl mx-auto">
            See how Okio can transform guidance for your team, customers, or visitors.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:hello@okio.ai?subject=Demo Request&body=Hi, I'd like to schedule a demo for Okio. Our use case is..."
              className="px-10 py-4 bg-white text-[#1a1a18] hover:bg-white/90 font-medium rounded-xl transition-colors text-base min-w-[200px]"
            >
              Schedule a Demo
              <ArrowRightIcon className="inline-block ml-2 h-4 w-4" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=ai.okio.app"
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-4 bg-transparent border border-white/50 hover:bg-white/10 text-white font-medium rounded-xl transition-colors text-base min-w-[200px]"
            >
              Try the App
            </a>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="py-12 px-6 bg-[#1a1a18] text-white/60">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img 
            src="/logo_white.webp" 
            alt="OKIO" 
            className="h-6 w-auto opacity-60"
          />
          <p className="text-sm">
            Questions?{' '}
            <button onClick={() => navigate('/discover')} className="text-white/80 hover:text-white underline">
              Learn more
            </button>
          </p>
        </div>
      </footer>
      </div>{/* End scrollable container */}
    </div>
  );
};

export default WelcomePage;
